import { fetchV2WithAuth } from './apiAuth';
import { isTestModeBypass } from './testMode';

// Support tickets now go through the FranchiseApi v2 backend (/v2/jira-tickets/*) instead of the
// AgencyOS carrier API, whose routes were open to the internet: anyone could file a ticket in our
// name, and reads were scoped by an `email` query parameter the caller chose (WIPE-1371). The
// backend takes the reporter from the verified token, so nothing here says who the caller is.
//
// Which project and issue type a ticket lands in is likewise the backend's to decide, so those
// literals are gone from the call sites: a form names itself with `destination` and the backend
// turns that into a project, an issue type and the receiving team's labels.
const API_BASE_URL = import.meta.env.VITE_FRANCHISE_API_URL;

/**
 * The forms this API serves, as the backend's TICKET_DESTINATIONS keys.
 *
 * 'franchise-request' is the WEFR service desk — equipment orders, carrier requests, data updates,
 * escalations — and is what a payload without a destination gets. AgencyOS's own bugs and feature
 * requests go to the WIPE product backlog instead (WIPE-1515).
 */
export type TicketDestination = 'franchise-request' | 'agencyos-product';

// A file already uploaded to S3, identified by the key the backend handed out for it.
export interface TicketAttachment {
  key: string;
  name: string;
}

export interface Ticket {
  key: string;
  summary: string;
  status: string;
  statusCategory: string;
  priority: string;
  category: string | null;
  created: string;
  updated: string;
  url: string;
}

export interface TicketComment {
  id: string;
  author: string;
  body: string;
  created: string;
}

export interface CreateTicketPayload {
  summary: string;
  description?: string;
  // Which form is submitting. Omit for the franchise service desk.
  destination?: TicketDestination;
  // The category the form offered, which the backend maps onto the destination project's own
  // category field. Sent as the plain label, so no Jira option id appears in this app.
  category?: string;
  assigneeId?: string;
  // Jira account ids, not addresses.
  watchers?: string[];
  customFields?: Record<string, unknown>;
  attachments?: TicketAttachment[];
  // Names a recipient list the backend holds; the addresses stay server-side.
  notify?: string;
  // Structured line items for the equipment-order notification, so its email can be
  // reformatted without a site deploy.
  orderDetails?: unknown;
}

interface UploadUrlResponse {
  uploadId: string;
  files: { name: string; key: string; uploadUrl: string }[];
}

class JiraTicketsApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL || '';
  }

  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    if (!this.baseUrl) throw new Error('Support ticket API not configured');

    // Every route here is behind requireAuth, and a test-mode session holds no bearer token, so
    // in the E2E build these can only 401. Refuse locally rather than putting an unanswerable
    // request on the production API (WIPE-1190).
    if (isTestModeBypass()) throw new Error('Support tickets unavailable in test mode');

    // fetchV2WithAuth attaches auth + x-api-key and replays once on an expired token.
    const response = await fetchV2WithAuth(`${this.baseUrl}${path}`, options);

    if (!response.ok) {
      throw new Error(await errorMessage(response));
    }

    return response.json();
  }

  async createTicket(payload: CreateTicketPayload): Promise<{ ticketKey: string; ticketUrl: string }> {
    return this.request('/v2/jira-tickets', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // Presign, PUT, and return the keys to send back on submit. The presigned URL signs the
  // Content-Type, so the PUT must send the byte-identical value (or S3 403s). Keys are
  // namespaced per caller server-side, which is what lets createTicket verify an attachment
  // belongs to the person submitting it.
  async uploadAttachments(files: File[]): Promise<TicketAttachment[]> {
    if (files.length === 0) return [];

    const { files: presigned } = await this.request<UploadUrlResponse>('/v2/jira-tickets/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        files: files.map(f => ({ name: f.name, type: f.type, size: f.size })),
      }),
    });

    await Promise.all(
      presigned.map(async (target, i) => {
        const file = files[i];
        const response = await fetch(target.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!response.ok) {
          throw new Error(`Upload failed for ${file.name}: ${response.statusText}`);
        }
      })
    );

    return presigned.map(f => ({ key: f.key, name: f.name }));
  }

  // Open tickets the caller reported. `asEmail` reads someone else's — the support view — and is
  // refused for anyone without the admin capability, so callers pass it only when a user override
  // is actually in effect.
  async getMyTickets(asEmail?: string | null): Promise<{ tickets: Ticket[]; total: number }> {
    return this.request(`/v2/jira-tickets/my-tickets${emailQuery(asEmail)}`);
  }

  async getComments(
    ticketKey: string,
    asEmail?: string | null
  ): Promise<{ description: string; comments: TicketComment[] }> {
    return this.request(`/v2/jira-tickets/${encodeURIComponent(ticketKey)}/comments${emailQuery(asEmail)}`);
  }

  async addComment(ticketKey: string, comment: string, asEmail?: string | null): Promise<TicketComment> {
    return this.request(`/v2/jira-tickets/${encodeURIComponent(ticketKey)}/comments`, {
      method: 'POST',
      // The override travels in the body here rather than the query string, this being a POST.
      body: JSON.stringify({ comment, ...(asEmail ? { email: asEmail } : {}) }),
    });
  }
}

function emailQuery(asEmail?: string | null): string {
  return asEmail ? `?email=${encodeURIComponent(asEmail)}` : '';
}

// The v2 error contract is { errors: { general?, fields? } }. Reduce it to the one line these
// flows show the user; a non-JSON body (a gateway error, say) falls back to the status.
async function errorMessage(response: Response): Promise<string> {
  const fallback = `Request failed: ${response.status}`;
  try {
    const body = await response.json();
    const errors = body?.errors;
    if (errors && typeof errors === 'object') {
      const fields: Record<string, string> = errors.fields ?? {};
      return errors.general ?? Object.values(fields)[0] ?? fallback;
    }
    return body?.error ?? fallback;
  } catch {
    return fallback;
  }
}

export const jiraTicketsApi = new JiraTicketsApiClient();
